import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { ApiRequestError } from '@/services/api';
import {
  CONFIRMATION_PHRASE,
  adminService,
  type AuditPurgePreview,
  type DraftPreview,
  type RangeInput,
} from '@/services/admin.service';
import { v } from '@/theme/tokens';

/** The two vocabularies are deliberately spelled out rather than shortened
 * to a number of days: «старше 90 дней» and «за последние 90 дней» select
 * opposite halves of the log. */
interface PeriodOption {
  key: string;
  label: string;
  range: RangeInput;
}

const DRAFT_OPTIONS: PeriodOption[] = [
  { key: 'all', label: 'Все черновики', range: { mode: 'ALL' } },
  { key: 'older-7', label: 'Старше 7 дней', range: { mode: 'OLDER_THAN', days: 7 } },
  { key: 'older-30', label: 'Старше 30 дней', range: { mode: 'OLDER_THAN', days: 30 } },
  { key: 'older-90', label: 'Старше 90 дней', range: { mode: 'OLDER_THAN', days: 90 } },
  { key: 'older-180', label: 'Старше 6 месяцев', range: { mode: 'OLDER_THAN', days: 180 } },
  { key: 'older-365', label: 'Старше 1 года', range: { mode: 'OLDER_THAN', days: 365 } },
  { key: 'custom', label: 'Выбранный период', range: { mode: 'CUSTOM' } },
];

const AUDIT_OPTIONS: PeriodOption[] = [
  { key: 'last-7', label: 'За последние 7 дней', range: { mode: 'LAST_DAYS', days: 7 } },
  { key: 'last-30', label: 'За последние 30 дней', range: { mode: 'LAST_DAYS', days: 30 } },
  { key: 'last-90', label: 'За последние 90 дней', range: { mode: 'LAST_DAYS', days: 90 } },
  { key: 'older-180', label: 'Старше 6 месяцев', range: { mode: 'OLDER_THAN', days: 180 } },
  { key: 'older-365', label: 'Старше 1 года', range: { mode: 'OLDER_THAN', days: 365 } },
  { key: 'custom', label: 'Выбранный период', range: { mode: 'CUSTOM' } },
  { key: 'all', label: 'Очистить всё', range: { mode: 'ALL' } },
];

const AUDIT_CATEGORY_LABELS: Record<string, string> = {
  imports: 'Импорты',
  financial: 'Финансовые',
  users_roles: 'Пользователи и роли',
  extraordinary: 'Внеплановые расходы',
  telegram: 'Telegram',
  other: 'Прочее',
};

const DRAFT_STATUS_LABELS: Record<string, string> = {
  UPLOADED: 'Загружен',
  PARSING: 'Разбор',
  VALIDATING: 'Проверка',
  NEEDS_MAPPING: 'Требует сопоставления',
  READY: 'Готов к подтверждению',
  FAILED: 'Ошибка',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function rangeCaption(from: string | null | undefined, to: string | null | undefined): string {
  if (!from && !to) return 'Без ограничения по датам';
  if (from && to) return `${formatDate(from)} — ${formatDate(to)}`;
  if (to) return `по ${formatDate(to)}`;
  return `с ${formatDate(from)}`;
}

const inputStyle = {
  padding: '8px 10px',
  borderRadius: 9,
  border: `1px solid ${v('line')}`,
  background: v('surface'),
  color: v('ink'),
  fontSize: 13,
} as const;

/**
 * Настройки → Администрирование.
 *
 * Owner-only, and the server re-checks that on every call. Nothing is ever
 * deleted straight from a period choice: the flow is always choose → preview
 * → read the exact count and range → type «УДАЛИТЬ».
 */
export function AdministrationSection() {
  const wide = useIsWideScreen();

  return (
    <Card>
      <SectionTitle right={<Badge tone="warning">только владелец</Badge>}>
        Администрирование
      </SectionTitle>

      <p style={{ fontSize: 12.5, color: v('ink-muted'), marginTop: 0 }}>
        Необратимые операции. Подтверждённые отчёты, финансовые значения, периоды и пользователи
        никогда не удаляются — очищаются только незавершённые черновики и записи журнала.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: wide ? 'repeat(2, 1fr)' : '1fr',
          gap: 14,
          marginTop: 6,
        }}
      >
        <DraftsBlock />
        <AuditBlock />
      </div>
    </Card>
  );
}

/** Period choice + resolved range, shared by both danger blocks. */
function useCleanupState(options: PeriodOption[]) {
  const [optionKey, setOptionKey] = useState(options[0].key);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const option = options.find((o) => o.key === optionKey) ?? options[0];
  const range: RangeInput =
    option.range.mode === 'CUSTOM'
      ? {
          mode: 'CUSTOM',
          date_from: customFrom ? `${customFrom}T00:00:00Z` : null,
          date_to: customTo ? `${customTo}T23:59:59Z` : null,
        }
      : option.range;

  return { optionKey, setOptionKey, option, range, customFrom, setCustomFrom, customTo, setCustomTo };
}

function blockStyle() {
  return {
    border: `1px solid ${v('line')}`,
    borderRadius: 12,
    padding: 14,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  };
}

function PeriodPicker({
  options,
  value,
  onChange,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  label,
}: {
  options: PeriodOption[];
  value: string;
  onChange: (key: string) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  label: string;
}) {
  const isCustom = options.find((o) => o.key === value)?.range.mode === 'CUSTOM';
  return (
    <>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
          Период
        </span>
        <select
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, width: '100%' }}
        >
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {isCustom && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ flex: 1, minWidth: 130 }}>
            <span style={{ display: 'block', fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
              Дата от
            </span>
            <input
              type="date"
              aria-label="Дата от"
              value={customFrom}
              onChange={(e) => onCustomFrom(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </label>
          <label style={{ flex: 1, minWidth: 130 }}>
            <span style={{ display: 'block', fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
              Дата до
            </span>
            <input
              type="date"
              aria-label="Дата до"
              value={customTo}
              onChange={(e) => onCustomTo(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </label>
        </div>
      )}
    </>
  );
}

function ConfirmField({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
        Для подтверждения введите «{CONFIRMATION_PHRASE}»
      </span>
      <input
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={CONFIRMATION_PHRASE}
        style={{ ...inputStyle, width: '100%' }}
      />
    </label>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof ApiRequestError ? error.message : 'Не удалось выполнить операцию';
  return (
    <div role="alert" style={{ color: v('negative'), fontSize: 12.5 }}>
      {message}
    </div>
  );
}

// --- drafts ------------------------------------------------------------------

function DraftsBlock() {
  const client = useQueryClient();
  const state = useCleanupState(DRAFT_OPTIONS);
  const [preview, setPreview] = useState<DraftPreview | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [done, setDone] = useState<number | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => adminService.previewDrafts(state.range),
    onSuccess: (data) => {
      setPreview(data);
      setDone(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminService.deleteDrafts(state.range, confirmation),
    onSuccess: (data) => {
      setDone(data.total);
      setPreview(null);
      setConfirmation('');
      void client.invalidateQueries({ queryKey: ['imports'] });
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  return (
    <div style={blockStyle()}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>Черновики</div>
      <p style={{ fontSize: 12, color: v('ink-muted'), margin: 0 }}>
        Загруженные, но не подтверждённые импорты. Подтверждённые отчёты и их файлы не удаляются.
      </p>

      <PeriodPicker
        options={DRAFT_OPTIONS}
        value={state.optionKey}
        onChange={(key) => {
          state.setOptionKey(key);
          setPreview(null);
          setDone(null);
        }}
        customFrom={state.customFrom}
        customTo={state.customTo}
        onCustomFrom={state.setCustomFrom}
        onCustomTo={state.setCustomTo}
        label="Период удаления черновиков"
      />

      <Button onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
        {previewMutation.isPending ? 'Проверка…' : 'Проверить'}
      </Button>
      <ErrorLine error={previewMutation.error} />

      {preview && (
        <div
          style={{
            background: v('panel'),
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 12.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div>
            Найдено черновиков: <b>{preview.imports}</b>
          </div>
          {preview.orphan_files > 0 && <div>Файлов без ссылок: {preview.orphan_files}</div>}
          {Object.entries(preview.breakdown).map(([status, count]) => (
            <div key={status} style={{ color: v('ink-muted') }}>
              {DRAFT_STATUS_LABELS[status] ?? status}: {count}
            </div>
          ))}
          <div style={{ color: v('ink-muted') }}>{rangeCaption(preview.date_from, preview.date_to)}</div>
        </div>
      )}

      {preview && preview.imports > 0 && (
        <>
          {state.option.range.mode === 'ALL' && (
            <div
              style={{
                background: v('warning-soft'),
                color: v('warning'),
                borderRadius: 10,
                padding: '9px 11px',
                fontSize: 12.5,
              }}
            >
              Будут удалены все черновики без ограничения по дате.
            </div>
          )}
          <ConfirmField
            value={confirmation}
            onChange={setConfirmation}
            label="Подтверждение удаления черновиков"
          />
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Удаление…' : 'Удалить черновики'}
          </Button>
          <ErrorLine error={deleteMutation.error} />
        </>
      )}

      {done !== null && (
        <div style={{ color: v('positive'), fontSize: 12.5 }}>Удалено объектов: {done}</div>
      )}
    </div>
  );
}

// --- audit -------------------------------------------------------------------

function AuditBlock() {
  const client = useQueryClient();
  const state = useCleanupState(AUDIT_OPTIONS);
  const [preview, setPreview] = useState<AuditPurgePreview | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [done, setDone] = useState<number | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => adminService.previewAuditPurge(state.range),
    onSuccess: (data) => {
      setPreview(data);
      setDone(null);
    },
  });

  const purgeMutation = useMutation({
    mutationFn: () => adminService.purgeAudit(state.range, confirmation),
    onSuccess: (data) => {
      setDone(data.total);
      setPreview(null);
      setConfirmation('');
      void client.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  const isAll = state.option.range.mode === 'ALL';

  return (
    <div style={blockStyle()}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>Журнал аудита</div>
      <p style={{ fontSize: 12, color: v('ink-muted'), margin: 0 }}>
        «За последние N дней» удаляет свежие записи, «старше N дней» — наоборот, давние. След самой
        очистки сохраняется отдельно и не удаляется.
      </p>

      <PeriodPicker
        options={AUDIT_OPTIONS}
        value={state.optionKey}
        onChange={(key) => {
          state.setOptionKey(key);
          setPreview(null);
          setDone(null);
        }}
        customFrom={state.customFrom}
        customTo={state.customTo}
        onCustomFrom={state.setCustomFrom}
        onCustomTo={state.setCustomTo}
        label="Период очистки журнала"
      />

      <Button onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
        {previewMutation.isPending ? 'Проверка…' : 'Проверить'}
      </Button>
      <ErrorLine error={previewMutation.error} />

      {preview && (
        <div
          style={{
            background: v('panel'),
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 12.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div>
            Будет удалено: <b>{preview.total}</b> записей
          </div>
          <div style={{ color: v('ink-muted') }}>{rangeCaption(preview.date_from, preview.date_to)}</div>
          {Object.entries(preview.breakdown).map(([category, count]) => (
            <div key={category} style={{ color: v('ink-muted') }}>
              {AUDIT_CATEGORY_LABELS[category] ?? category}: {count}
            </div>
          ))}
        </div>
      )}

      {preview && preview.total > 0 && (
        <>
          {isAll && (
            <div
              style={{
                background: v('negative-soft'),
                color: v('negative'),
                borderRadius: 10,
                padding: '9px 11px',
                fontSize: 12.5,
              }}
            >
              Журнал будет очищен полностью. Восстановить записи будет невозможно.
            </div>
          )}
          <ConfirmField
            value={confirmation}
            onChange={setConfirmation}
            label="Подтверждение очистки журнала"
          />
          <Button
            variant="danger"
            onClick={() => purgeMutation.mutate()}
            disabled={purgeMutation.isPending}
          >
            {purgeMutation.isPending ? 'Очистка…' : 'Очистить журнал'}
          </Button>
          <ErrorLine error={purgeMutation.error} />
        </>
      )}

      {done !== null && (
        <div style={{ color: v('positive'), fontSize: 12.5 }}>Удалено записей: {done}</div>
      )}
    </div>
  );
}
